/**
 * Quota Display Component
 * 
 * Displays current usage quotas and remaining limits with:
 * - Progress bars for each time window
 * - Reset countdown timers
 * - Warning indicators for high utilization
 * - Developer mode unlimited status
 * 
 * Implements Requirements: 5.5, 5.6
 */

import React, { useState, useEffect } from 'react';
import { QuotaStatus } from '../../services/QuotaAnalyticsService';
import quotaAnalyticsService from '../../services/QuotaAnalyticsService';
import styles from './QuotaDisplay.module.css';

interface QuotaDisplayProps {
  quotaStatus: QuotaStatus;
}

interface TimeWindow {
  key: string;
  label: string;
  resetKey: keyof QuotaStatus['reset_times'];
}

const TIME_WINDOWS: TimeWindow[] = [
  { key: 'minute', label: 'Per Minute', resetKey: 'seconds_until_minute_reset' },
  { key: 'hour', label: 'Per Hour', resetKey: 'seconds_until_hour_reset' },
  { key: 'day', label: 'Per Day', resetKey: 'seconds_until_day_reset' },
  { key: 'month', label: 'Per Month', resetKey: 'seconds_until_month_reset' }
];

export const QuotaDisplay: React.FC<QuotaDisplayProps> = ({ quotaStatus }) => {
  const [resetTimes, setResetTimes] = useState(quotaStatus.reset_times);

  // Update countdown timers every second
  useEffect(() => {
    const interval = setInterval(() => {
      setResetTimes(prev => ({
        ...prev,
        seconds_until_minute_reset: Math.max(0, prev.seconds_until_minute_reset - 1),
        seconds_until_hour_reset: Math.max(0, prev.seconds_until_hour_reset - 1),
        seconds_until_day_reset: Math.max(0, prev.seconds_until_day_reset - 1),
        seconds_until_month_reset: Math.max(0, prev.seconds_until_month_reset - 1)
      }));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Reset timers when quotaStatus changes
  useEffect(() => {
    setResetTimes(quotaStatus.reset_times);
  }, [quotaStatus.reset_times]);

  // Developer mode - show unlimited status
  if (quotaStatus.tier?.toLowerCase() === 'developer') {
    return (
      <div className={styles.container}>
        <div className={styles.unlimitedBanner}>
          <div className={styles.unlimitedIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <div className={styles.unlimitedContent}>
            <h3 className={styles.unlimitedTitle}>Unlimited Access</h3>
            <p className={styles.unlimitedDescription}>
              Developer mode is enabled. You have unlimited API access.
              Usage is still tracked for analytics purposes.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Safety check - ensure quota object exists and has required structure
  if (!quotaStatus.quota || !quotaStatus.quota.remaining || !quotaStatus.quota.utilization) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <p>Unable to load quota information. Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  const { quota } = quotaStatus;

  return (
    <div className={styles.container}>
      {/* Warnings */}
      {quotaStatus.warnings && quotaStatus.warnings.length > 0 && (
        <div className={styles.warnings}>
          {quotaStatus.warnings.map((warning, index) => (
            <div
              key={index}
              className={`${styles.warning} ${styles[warning.level]}`}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 12.5a5.5 5.5 0 110-11 5.5 5.5 0 010 11zM8 4a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 018 4zm0 8a1 1 0 100-2 1 1 0 000 2z"/>
              </svg>
              <span>{warning.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Quota Grid */}
      <div className={styles.quotaGrid}>
        {TIME_WINDOWS.map(({ key, label, resetKey }) => {
          const tokenKey = `tokens_${key}`;
          const requestKey = `requests_${key}`;
          
          const tokenUtilization = quota.utilization?.[tokenKey] ?? 0;
          const requestUtilization = quota.utilization?.[requestKey] ?? 0;
          
          const tokenRemaining = quota.remaining?.[tokenKey] ?? 0;
          const requestRemaining = quota.remaining?.[requestKey] ?? 0;
          
          const tokenLimit = quota.limits?.tokens?.[key] ?? 0;
          const requestLimit = quota.limits?.requests?.[key] ?? 0;
          
          const tokenUsed = quota.current_usage?.[key] ?? 0;
          const requestUsed = quota.current_requests?.[key] ?? 0;
          
          const resetSeconds = resetTimes[resetKey] as number ?? 0;

          return (
            <div key={key} className={styles.quotaCard}>
              <div className={styles.quotaHeader}>
                <span className={styles.quotaLabel}>{label}</span>
                <span className={styles.resetTime}>
                  Resets in {quotaAnalyticsService.formatTimeUntilReset(resetSeconds)}
                </span>
              </div>

              {/* Token Quota */}
              <div className={styles.quotaItem}>
                <div className={styles.quotaItemHeader}>
                  <span className={styles.quotaItemLabel}>Tokens</span>
                  <span className={styles.quotaItemValue}>
                    {quotaAnalyticsService.formatTokens(tokenUsed)} / {quotaAnalyticsService.formatTokens(tokenLimit as number)}
                  </span>
                </div>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{
                      width: `${Math.min(100, tokenUtilization)}%`,
                      backgroundColor: quotaAnalyticsService.getUtilizationColor(tokenUtilization)
                    }}
                  />
                </div>
                <div className={styles.quotaItemFooter}>
                  <span className={styles.remaining}>
                    {quotaAnalyticsService.formatTokens(tokenRemaining as number)} remaining
                  </span>
                  <span
                    className={styles.percentage}
                    style={{ color: quotaAnalyticsService.getUtilizationColor(tokenUtilization) }}
                  >
                    {quotaAnalyticsService.formatPercentage(tokenUtilization)}
                  </span>
                </div>
              </div>

              {/* Request Quota */}
              <div className={styles.quotaItem}>
                <div className={styles.quotaItemHeader}>
                  <span className={styles.quotaItemLabel}>Requests</span>
                  <span className={styles.quotaItemValue}>
                    {requestUsed} / {requestLimit}
                  </span>
                </div>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{
                      width: `${Math.min(100, requestUtilization)}%`,
                      backgroundColor: quotaAnalyticsService.getUtilizationColor(requestUtilization)
                    }}
                  />
                </div>
                <div className={styles.quotaItemFooter}>
                  <span className={styles.remaining}>
                    {requestRemaining} remaining
                  </span>
                  <span
                    className={styles.percentage}
                    style={{ color: quotaAnalyticsService.getUtilizationColor(requestUtilization) }}
                  >
                    {quotaAnalyticsService.formatPercentage(requestUtilization)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QuotaDisplay;
